import { lazy, useEffect } from 'react';
import { isBoolean, isString } from 'lodash';
import { push } from 'redux-first-history';
import { useDispatch, useSelector } from '@client/hooks/redux.hooks';
import { getSessionUserAdminStatus, getSessionUserState } from '@client/selectors/helpers/login';
import { getSelectedProfileType } from '@client/selectors/helpers/profile';
import { Loader } from '@client/components/common/Loader';
import { ProfileTypes } from '@sda/enums';
import type { WrapperProps } from '../interfaces';

const LoginWrapperLoader = lazy(() => import('@client/components/Main/Login/LoginWrapper'));
const CreateProfileModalLoader = lazy(() => import('@client/components/Main/Profiles/CreateProfile/CreateProfilePage'));

export const ComponentWrapper = <C extends React.ElementType>({
  adminOnly, requiresLogin, component, path,
  history, location, staticContext, match,
  requiresProfile,
  ...restProps
}: WrapperProps<C>) => {
  const dispatch = useDispatch();

  const isAdmin = useSelector(getSessionUserAdminStatus);
  const userState = useSelector(getSessionUserState);

  const selectedProfileType = useSelector(getSelectedProfileType);

  useEffect(() => {
    if (userState !== null) {
      if (requiresLogin === false && userState) {
        dispatch(push('/'));
      }

      if (requiresLogin && adminOnly && !isAdmin) {
        dispatch(push('/'));
      }
    }
  }, [adminOnly, dispatch, isAdmin, requiresLogin, userState]);

  const { params = {} } = match || {};

  if (requiresLogin && !userState) {
    return (
      <Loader
        key="login"
        {...params}
        load={LoginWrapperLoader}
      />
    );
  }

  if (requiresProfile && !isAdmin && (
    (isBoolean(requiresProfile) && (!selectedProfileType || selectedProfileType === ProfileTypes.PENDING))
    || (isString(requiresProfile) && selectedProfileType !== requiresProfile)
  )) {
    return (
      <Loader
        key="create-profile"
        {...params}
        standalone
        load={CreateProfileModalLoader}
      />
    );
  }

  if (component) {
    const Component = component as any;

    return (
      <Component
        pathname={location.pathname}
        {...params}
        {...restProps}
      />
    );
  }

  return (
    <Loader
      pathname={location.pathname}
      {...params}
      {...restProps}
    />
  );
};
